import React, { PureComponent } from 'react';
import { withRouter } from 'react-router-dom';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';
import { Alert } from 'reactstrap';

import { APIgetFileStatuses } from '../../api/Files';

import { showModal } from '../../actions';

import AdminFileStatusesHeader from '../../components/Admin/FileStatuses/Header';
import AdminFileStatusesList from '../../components/Admin/FileStatuses/List';
import AdminFileStatusesFooter from '../../components/Admin/FileStatuses/Footer';
import { handleComponentParams, handleURLParams } from '../../helpers/URLParams';

import { getVisibleItemsText } from '../../helpers';
import { getSetting, getUserSetting } from '../../helpers/Settings';

import Loading from '../../components/Loading';
import ErrorBoundary from '../../components/ErrorBoundary';
import HelpIcon from '../../components/HelpIcon';
import EmptyFileStatus from '../EmptyContainers/FileStatus';
import TrialNotice from '../../components/TrialNotice';
import SettingsItemsWrapper from '../../components/Admin/Settings/Item/ItemsWrapper';
import SettingsEditButtons from '../../components/Admin/Settings/EditButtons';

class AdminFileStatuses extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      statuses: [],
      statusesTotal: 0,
      page: 1,
      perPage: getUserSetting('admin_per_page') || 20,
      orderBy: 'name',
      order: 'asc',
      loading: true,
      timestamp: new Date().toISOString(),
    };

    this.handleChangeOrder = this.handleChangeOrder.bind(this);
    this.handleChangePage = this.handleChangePage.bind(this);
    this.handleChangePerPage = this.handleChangePerPage.bind(this);
    this.handleAddStatus = this.handleAddStatus.bind(this);
    this.loadFileStatuses = this.loadFileStatuses.bind(this);
    this.updateStatusOnList = this.updateStatusOnList.bind(this);
    this.deleteStatusFromList = this.deleteStatusFromList.bind(this);
  }

  componentDidUpdate(prevProps) {
    const { statuses } = this.props;
    const { orderBy, order } = this.state;
    const { statuses: prevStatuses } = prevProps;

    if (statuses !== prevStatuses) {
      this.loadFileStatuses(true);
    }

    handleComponentParams(
      false,
      ['page', 'perPage', 'orderBy', 'order'],
      prevProps,
      this.setState.bind(this),
      this.loadFileStatuses,
      { orderBy, order, perPage: getUserSetting('admin_per_page') || 20 },
    );
  }

  componentDidMount() {
    const { history, location } = this.props;
    const { orderBy, order } = this.state;

    const queryParams = new URLSearchParams(location.search);

    handleComponentParams(
      true,
      ['page', 'perPage', 'orderBy', 'order'],
      null,
      this.setState.bind(this),
      this.loadFileStatuses,
      { orderBy, order, perPage: getUserSetting('admin_per_page') || 20 },
    );

    if (!queryParams.get('orderBy')) {
      handleURLParams({ orderBy }, history);
    }

    if (!queryParams.get('order')) {
      handleURLParams({ order }, history);
    }
  }

  loadFileStatuses(silent = false) {
    const {
      page,
      perPage,
      orderBy,
      order,
    } = this.state;

    if (!silent) {
      this.setState({ loading: true });
    }

    APIgetFileStatuses({
      page,
      per_page: perPage,
      order_by: orderBy,
      order,
    }).then((response) => {
      const { data } = response;
      if (data) {
        this.setState({
          loading: false,
          statuses: data.data,
          statusesTotal: data.meta.total,
        });
      }
    });
  }

  updateStatusOnList(status) {
    const { statuses } = this.state;

    const idx = statuses.findIndex(s => (s.id === status.id));
    if (idx >= 0) {
      statuses[idx] = status;
      this.setState({
        statuses,
        timestamp: new Date().toISOString(),
      });
    }
  }

  deleteStatusFromList(status) {
    this.setState(prevState => ({
      statuses: prevState.statuses.filter(s => (s.id !== status.id)),
    }));
    this.loadFileStatuses(true);
  }

  handleChangeOrder(orderBy, order) {
    const { history } = this.props;
    handleURLParams({ orderBy, order, page: 1 }, history);

    this.setState({
      orderBy,
      order,
      page: 1,
    });
  }

  handleChangePage(page) {
    const { history } = this.props;
    handleURLParams({ page }, history);

    this.setState({ page });
  }

  handleChangePerPage(perPage) {
    const { history } = this.props;
    handleURLParams({ perPage, page: 1 }, history);

    this.setState({ perPage, page: 1 });
  }

  handleAddStatus(e) {
    const { dispatch } = this.props;

    if (e) {
      e.preventDefault();
    }

    dispatch(showModal('FileStatusEditModal', {
      className: 'modal-new-status',
      afterCreateStatus: () => {
        this.loadFileStatuses(true);
      },
    }));
  }

  render() {
    const {
      intl,
      saving,
      handleSave,
      unsaved,
    } = this.props;
    const {
      page,
      perPage,
      statusesTotal,
      loading,
    } = this.state;

    return (
      <>
        <div className="settings-inner-content">
          <AdminFileStatusesHeader
            statusesTotal={statusesTotal}
            statusesVisible={getVisibleItemsText(page, perPage, statusesTotal)}
            loading={loading}
            handleAddStatus={this.handleAddStatus}
          />
          <TrialNotice feature="file-status" className="pl-0" />
          <p>
            {intl.formatMessage({ defaultMessage: 'Create and manage your file statuses. File statuses allow you to create optional statuses for your files for additional filtering. For example: Approved or Rejected.' })}
            <HelpIcon
              id="help-file-statuses"
              url="https://help.stockpress.co/en/articles/5937871-creating-file-statuses"
            />
          </p>
          <SettingsItemsWrapper
            {...this.props}
          />
          {!loading && statusesTotal === 0 && getSetting('file_status_required') && (
            <Alert color="danger">
              {intl.formatMessage({ defaultMessage: 'You\'ve enabled the <strong>Require statuses</strong> option, but no statuses are currently available. Please create at least one status to use this feature properly.' })}
            </Alert>
          )}
          {loading ? (
            <Loading />
          ) : (
            <>
              {statusesTotal > 0 ? (
                <>
                  <ErrorBoundary>
                    <AdminFileStatusesList
                      {...this.state}
                      loadStatuses={this.loadStatuses}
                      handleChangeOrder={this.handleChangeOrder}
                      updateStatusOnList={this.updateStatusOnList}
                      deleteStatusFromList={this.deleteStatusFromList}
                    />
                  </ErrorBoundary>
                  <ErrorBoundary>
                    <AdminFileStatusesFooter
                      {...this.state}
                      handleChangePage={this.handleChangePage}
                      handleChangePerPage={this.handleChangePerPage}
                    />
                  </ErrorBoundary>
                </>
              ) : (
                <EmptyFileStatus handleNewItem={this.handleAddStatus} />
              )}
            </>
          )}
        </div>
        <SettingsEditButtons
          saving={saving}
          handleSave={handleSave}
          unsaved={unsaved}
        />
      </>
    );
  }
}

const mapStateToProps = state => ({
  statuses: state.Files.statuses,
});

AdminFileStatuses.propTypes = {
  intl: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  statuses: PropTypes.array.isRequired,
  saving: PropTypes.bool.isRequired,
  handleSave: PropTypes.func.isRequired,
  unsaved: PropTypes.bool.isRequired,
};

export default withRouter(connect(mapStateToProps)(injectIntl(AdminFileStatuses)));
